import { Download, FileText, Check } from 'lucide-react';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import { RESOURCES } from '../data/content';

export default function ResourcesPage() {
  return (
    <>
      <Seo
        title="Free Resources for Tradespeople | Build My Trade"
        description="Free, practical downloads for tradespeople: a sample monthly content plan and a phone-photography guide for job photos. No sign-up, no email required."
        path="/resources"
      />
      <PageHero
        eyebrow="Free resources"
        title="Useful stuff, no strings attached"
        intro="Practical guides we give to customers — free for any tradesperson to download. No sign-up, no email address, no catch."
        crumbs={[{ label: 'Resources' }]}
        testid="resources-heading"
      />

      <section className="bg-offwhite" data-testid="resources-list">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-6 lg:grid-cols-2">
            {RESOURCES.map((r, i) => (
              <Reveal key={r.slug} delay={0.08 * i} className="h-full">
                <article
                  className="flex h-full flex-col rounded-2xl border border-hairline bg-white p-8 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-charcoal/30 hover:shadow-[0_24px_50px_-30px_rgba(36,39,33,0.25)] sm:p-10"
                  data-testid={`resource-card-${r.slug}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                      <FileText className="h-5 w-5 text-lime" aria-hidden="true" />
                    </span>
                    <span className="rounded-full bg-lime px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-charcoal">
                      {r.tag}
                    </span>
                  </div>
                  <h2 className="mt-6 font-heading text-xl font-bold tracking-tight text-charcoal sm:text-2xl">
                    {r.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-stonebrand">{r.description}</p>
                  <ul className="mt-6 flex-1 space-y-2.5 border-t border-hairline pt-6">
                    {r.includes.map((item, j) => (
                      <li key={j} className="flex gap-2.5 text-sm leading-relaxed text-charcoal/85">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-charcoal" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={r.file}
                    download={r.filename}
                    data-testid={`resource-download-${r.slug}`}
                    className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-charcoal px-6 py-3 font-heading text-sm font-bold text-offwhite transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-charcoal-surface"
                  >
                    <Download className="h-4 w-4 text-lime transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
                    Download free
                  </a>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-stonebrand">
              More guides are added as customers ask for them. If there’s something you’d like — quoting templates,
              review request wording, anything — ask and we’ll probably write it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal-deep bg-blueprint" data-testid="resources-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Like how we explain things? <span className="text-lime">Imagine the website.</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                This is how we write for our customers’ businesses too — plain, useful and straight to the point.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="resources-cta-quote" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
